/**
 * @file DatabaseConnections.js
 * WHAT: Technology-agnostic read-only dashboard showing all database connections across Oracle, MSSQL, MySQL, PostgreSQL, and Cassandra.
 * WHY: DBAs and admins need a single pane of glass to monitor connection health across all 5 database technologies without switching between technology-specific pages.
 */

import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import 'bootstrap/dist/css/bootstrap.min.css';
import { PostgresqlIcon } from './common';

/**
 * WHAT: Renders a unified, read-only connection status dashboard with per-technology stats cards, filters, and inline connectivity testing.
 * WHY: Enables operations teams to quickly identify failing connections across all technologies and navigate to the appropriate management page for remediation.
 * @param {Object} props
 * @param {Function} props.onNavigate - Callback to navigate to technology-specific management pages (e.g., 'oracle-connections').
 */
const DatabaseConnections = ({ onNavigate }) => {

  // State
  const [connections, setConnections] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testingConnection, setTestingConnection] = useState(null); // Track which connection is being tested

  // Test result modal state
  const [showTestResultModal, setShowTestResultModal] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    database_type: '',
    connection_status: '',
    is_active: '',
    search: ''
  });

  // Database type configuration
  const databaseTypes = [
    { value: 'ORACLE', label: 'Oracle', color: 'danger', icon: 'fa-database' },
    { value: 'MSSQL', label: 'SQL Server', color: 'warning', icon: 'fa-server' },
    { value: 'MYSQL', label: 'MySQL', color: 'success', icon: 'fa-database' },
    { value: 'POSTGRESQL', label: 'PostgreSQL', color: 'info', iconComponent: PostgresqlIcon },
    { value: 'CASSANDRA', label: 'Cassandra', color: 'primary', icon: 'fa-layer-group' }
  ];

  // View names for technology-specific management pages
  const managementViews = {
    'ORACLE': 'oracle-connections',
    'MSSQL': 'mssql-connections',
    'MYSQL': 'mysql-connections',
    'POSTGRESQL': 'postgresql-connections',
    'CASSANDRA': 'cassandra-connections'
  };

  // Fetch aggregated connections
  const fetchConnections = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (filters.database_type) params.database_type = filters.database_type;
      if (filters.connection_status) params.connection_status = filters.connection_status;
      if (filters.is_active) params.is_active = filters.is_active;
      if (filters.search) params.search = filters.search;

      const response = await api.databases.getAggregatedConnections(params);

      if (response.data.success) {
        setConnections(response.data.connections);
        setStats(response.data.stats);
      } else {
        setError('Failed to fetch connections');
      }
    } catch (err) {
      setError(`Error fetching connections: ${err.message}`);
      console.error('Error fetching connections:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnections();
  }, [filters.database_type, filters.connection_status, filters.is_active]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.search !== undefined) {
        fetchConnections();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Handle filter changes
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      database_type: '',
      connection_status: '',
      is_active: '',
      search: ''
    });
  };

  // Get type configuration
  const getTypeConfig = (type) => {
    return databaseTypes.find(t => t.value === type) || { label: type, color: 'secondary', icon: 'fa-database' };
  };

  // Navigate to management page
  const goToManagement = (type) => {
    const view = managementViews[type];
    if (view && onNavigate) {
      onNavigate(view);
    }
  };

  // Get database type display name and color
  const getDatabaseTypeInfo = (type) => {
    const typeMap = {
      'ORACLE': { label: 'Oracle', color: '#c74634' },
      'MSSQL': { label: 'SQL Server', color: '#0078d4' },
      'MYSQL': { label: 'MySQL', color: '#00758f' },
      'POSTGRESQL': { label: 'PostgreSQL', color: '#336791' },
      'CASSANDRA': { label: 'Cassandra', color: '#1287b1' }
    };
    return typeMap[type] || { label: type, color: '#6c757d' };
  };

  // Test connection
  const testConnection = async (conn) => {
    const connectionId = conn.id;
    setTestingConnection(connectionId);
    setError(null);

    const dbTypeInfo = getDatabaseTypeInfo(conn.database_type);

    try {
      let response;

      // Call the appropriate test API based on database type
      switch (conn.database_type) {
        case 'ORACLE':
          response = await api.databases.testOracleConnection(connectionId);
          break;
        case 'MSSQL':
          response = await api.databases.testMssqlSourceConnection(connectionId);
          break;
        case 'MYSQL':
          response = await api.databases.testMysqlSourceConnection(connectionId);
          break;
        case 'POSTGRESQL':
          response = await api.databases.testPostgresqlSourceConnection(connectionId);
          break;
        case 'CASSANDRA':
          response = await api.databases.testCassandraSourceConnection(connectionId);
          break;
        default:
          throw new Error(`Unknown database type: ${conn.database_type}`);
      }

      setTestResult({
        connectionName: conn.connection_name || 'Unknown',
        databaseType: dbTypeInfo.label,
        databaseTypeColor: dbTypeInfo.color,
        success: response.data.success,
        status: response.data.status || (response.data.success ? 'SUCCESS' : 'FAILED'),
        message: response.data.message,
        responseTime: response.data.response_time,
        databaseVersion: response.data.database_version,
        instanceName: response.data.instance_name,
        clusterName: response.data.cluster_name,
        cassandraVersion: response.data.cassandra_version,
        error: response.data.error
      });
      setShowTestResultModal(true);

      // Refresh the connections list to show updated status
      await fetchConnections();
    } catch (err) {
      console.error('Error testing connection:', err);
      setTestResult({
        connectionName: conn.connection_name || 'Unknown',
        databaseType: dbTypeInfo.label,
        databaseTypeColor: dbTypeInfo.color,
        success: false,
        status: 'FAILED',
        message: null,
        error: err.response?.data?.error || err.response?.data?.message || err.message
      });
      setShowTestResultModal(true);
      // Still refresh to show updated status
      await fetchConnections();
    } finally {
      setTestingConnection(null);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get status text class
  const getStatusClass = (status) => {
    const classes = {
      'SUCCESS': 'text-success',
      'FAILED': 'text-danger',
      'PENDING': 'text-warning',
      'NOT_TESTED': 'text-muted'
    };
    return classes[status] || 'text-muted';
  };

  // Check if any filters are active
  const hasActiveFilters = filters.database_type || filters.connection_status || filters.is_active || filters.search;

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">
            <i className="fas fa-plug me-2"></i>
            Database Connections Overview
          </h4>
          <small className="text-muted">
            Read-only status view of all database connections. Use technology-specific pages to manage connections.
          </small>
        </div>
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={fetchConnections}
          disabled={loading}
        >
          <i className={`fas fa-sync-alt me-1 ${loading ? 'fa-spin' : ''}`}></i>
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="row mb-3 g-2">
          {/* Total Card */}
          <div className="col">
            <div className="card border-secondary h-100">
              <div className="card-body text-center py-2 px-2">
                <h5 className="mb-0 text-secondary">{stats.ALL?.total || 0}</h5>
                <small style={{ fontSize: '0.7rem' }} className="text-muted">Total</small>
                <div className="mt-1" style={{ fontSize: '0.7rem' }}>
                  <span className="text-success me-1"><i className="fas fa-check"></i> {stats.ALL?.success || 0}</span>
                  <span className="text-danger"><i className="fas fa-times"></i> {stats.ALL?.failed || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Technology Cards */}
          {databaseTypes.map(type => {
            const typeStat = stats[type.value] || { total: 0, success: 0, failed: 0 };
            return (
              <div key={type.value} className="col">
                <div
                  className={`card border-${type.color} h-100`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => goToManagement(type.value)}
                  title={`Click to manage ${type.label} connections`}
                >
                  <div className="card-body text-center py-2 px-2">
                    <h5 className={`mb-0 text-${type.color}`}>{typeStat.total}</h5>
                    <small style={{ fontSize: '0.7rem' }} className="text-muted">{type.label}</small>
                    <div className="mt-1" style={{ fontSize: '0.7rem' }}>
                      <span className="text-success me-1"><i className="fas fa-check"></i> {typeStat.success}</span>
                      <span className="text-danger"><i className="fas fa-times"></i> {typeStat.failed}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="card shadow-sm mb-4">
        <div className="card-body py-2">
          <div className="row align-items-center g-2">
            <div className="col-md-3">
              <div className="input-group input-group-sm">
                <span className="input-group-text"><i className="fas fa-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search connections..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                value={filters.database_type}
                onChange={(e) => handleFilterChange('database_type', e.target.value)}
              >
                <option value="">All Technologies</option>
                {databaseTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                value={filters.connection_status}
                onChange={(e) => handleFilterChange('connection_status', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="SUCCESS">Success</option>
                <option value="FAILED">Failed</option>
                <option value="NOT_TESTED">Not Tested</option>
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                value={filters.is_active}
                onChange={(e) => handleFilterChange('is_active', e.target.value)}
              >
                <option value="">Active & Inactive</option>
                <option value="true">Active Only</option>
                <option value="false">Inactive Only</option>
              </select>
            </div>
            <div className="col-md-3 text-end">
              {hasActiveFilters && (
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={clearFilters}
                >
                  <i className="fas fa-times me-1"></i>
                  Clear Filters
                </button>
              )}
              <span className="ms-2 text-muted small">
                {connections.length} connection{connections.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      {/* Connections Table */}
      <div className="card shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading connections...</p>
            </div>
          ) : connections.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-plug fa-3x text-muted mb-3"></i>
              <p className="text-muted">No connections found.</p>
              {hasActiveFilters && (
                <button className="btn btn-outline-primary btn-sm" onClick={clearFilters}>
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>Technology</th>
                    <th>Connection Name</th>
                    <th>Host</th>
                    <th>Service/Database</th>
                    <th>Username</th>
                    <th>Status</th>
                    <th>Active</th>
                    <th>Last Tested</th>
                    <th className="text-center">Test</th>
                  </tr>
                </thead>
                <tbody>
                  {connections.map((conn, index) => {
                    const typeConfig = getTypeConfig(conn.database_type);
                    return (
                      <tr key={`${conn.database_type}-${conn.id}-${index}`}>
                        <td>
                          <span className={`badge bg-${typeConfig.color}`}>
                            {typeConfig.iconComponent ? (
                              <typeConfig.iconComponent size={14} className="me-1" />
                            ) : (
                              <i className={`fas ${typeConfig.icon} me-1`}></i>
                            )}
                            {typeConfig.label}
                          </span>
                        </td>
                        <td>
                          <strong>{conn.connection_name}</strong>
                          {conn.database_identifier && (
                            <small className="d-block text-muted">{conn.database_identifier}</small>
                          )}
                        </td>
                        <td>
                          {conn.host}
                          {conn.port && <small className="text-muted">:{conn.port}</small>}
                        </td>
                        <td>{conn.service_or_database || '-'}</td>
                        <td>{conn.username}</td>
                        <td>
                          <span className={getStatusClass(conn.connection_status)}>
                            <i className={`fas ${conn.connection_status === 'SUCCESS' ? 'fa-check-circle' : conn.connection_status === 'FAILED' ? 'fa-times-circle' : 'fa-question-circle'} me-1`}></i>
                            {conn.connection_status || 'NOT_TESTED'}
                          </span>
                          {conn.last_test_error && conn.connection_status === 'FAILED' && (
                            <i
                              className="fas fa-info-circle text-danger ms-1"
                              title={conn.last_test_error}
                              style={{ cursor: 'help' }}
                            ></i>
                          )}
                        </td>
                        <td>
                          {conn.is_active ? (
                            <span className="text-success"><i className="fas fa-check"></i></span>
                          ) : (
                            <span className="text-muted"><i className="fas fa-minus"></i></span>
                          )}
                        </td>
                        <td>
                          <small className="text-muted">{formatDate(conn.last_test_at)}</small>
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => testConnection(conn)}
                            disabled={testingConnection === conn.id}
                            title="Test Connection"
                          >
                            {testingConnection === conn.id ? (
                              <span className="spinner-border spinner-border-sm" role="status"></span>
                            ) : (
                              <i className="fas fa-plug"></i>
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-3 text-center">
        <small className="text-muted">
          <i className="fas fa-info-circle me-1"></i>
          Click on a technology card to manage connections. Use the test button to verify database connectivity.
        </small>
      </div>

      {/* Test Result Modal */}
      {showTestResultModal && testResult && (
        <div className="modal fade show" style={{
          display: 'block',
          backgroundColor: 'rgba(0,0,0,0.5)',
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 1060,
          overflow: 'auto'
        }}>
          <div style={{ padding: '20px' }}>
            <div className="modal-content" style={{ maxWidth: '600px', width: '100%', margin: '0 auto' }}>
              <div className="modal-header" style={{
                backgroundColor: testResult.success ? '#28a745' : '#dc3545',
                color: 'white'
              }}>
                <h5 className="modal-title">
                  <i className={`fas ${testResult.success ? 'fa-check-circle' : 'fa-times-circle'} me-2`}></i>
                  Connection Test Result
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowTestResultModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="text-center mb-4">
                  <i className={`fas ${testResult.success ? 'fa-check-circle text-success' : 'fa-times-circle text-danger'}`} style={{ fontSize: '4rem' }}></i>
                  <h4 className={`mt-3 ${testResult.success ? 'text-success' : 'text-danger'}`}>
                    {testResult.success ? 'Connection Successful' : 'Connection Failed'}
                  </h4>
                </div>

                <div className="card mb-3">
                  <div className="card-body">
                    <table className="table table-sm mb-0">
                      <tbody>
                        <tr>
                          <td className="fw-bold" style={{ width: '40%' }}>Connection Name</td>
                          <td>{testResult.connectionName}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Database Type</td>
                          <td><span className="badge" style={{ backgroundColor: testResult.databaseTypeColor }}>{testResult.databaseType}</span></td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Status</td>
                          <td>
                            <span className={`badge ${testResult.success ? 'bg-success' : 'bg-danger'}`}>
                              {testResult.status}
                            </span>
                          </td>
                        </tr>
                        {testResult.responseTime && (
                          <tr>
                            <td className="fw-bold">Response Time</td>
                            <td>{testResult.responseTime}</td>
                          </tr>
                        )}
                        {testResult.databaseVersion && (
                          <tr>
                            <td className="fw-bold">Database Version</td>
                            <td>{testResult.databaseVersion}</td>
                          </tr>
                        )}
                        {testResult.instanceName && (
                          <tr>
                            <td className="fw-bold">Instance Name</td>
                            <td>{testResult.instanceName}</td>
                          </tr>
                        )}
                        {testResult.clusterName && (
                          <tr>
                            <td className="fw-bold">Cluster Name</td>
                            <td>{testResult.clusterName}</td>
                          </tr>
                        )}
                        {testResult.cassandraVersion && (
                          <tr>
                            <td className="fw-bold">Cassandra Version</td>
                            <td>{testResult.cassandraVersion}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {testResult.message && (
                  <div className={`alert ${testResult.success ? 'alert-success' : 'alert-info'}`}>
                    <i className="fas fa-info-circle me-2"></i>
                    {testResult.message}
                  </div>
                )}

                {testResult.error && (
                  <div className="alert alert-danger">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <strong>Error:</strong> {testResult.error}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowTestResultModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseConnections;
